import { default as firebase, firestore, storage } from "common/Firebase";
import { Company } from "./models/Company";
import { LoginStore } from "modules/login/LoginStore";
import defaultImagem from "common/assets/images/thumb-default.png";
import { ProfileStore } from "modules/profile/ProfileStore";
import { createUserModel, User } from "modules/login/models/User";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { now } from "utils/FirebaseUtils";
import { FlashAutoRounded } from "@material-ui/icons";

/**
 * Service do perfil do Usuário.
 * @category Users
 * @hideconstructor
 */
class ProfileService {
  /**
   * Esta função retorna os dados do usuário logado na sessão atual.
   * @see {@link https://firebase.google.com/docs/reference/js/v8/firebase.auth.Auth#currentuser|Firebase - currentuser}
   */
  getAuthData(): firebase.User | null {
    return firebase.auth().currentUser;
  }

  getProviderData() {
    const user = this.getAuthData();
    return user ? user.providerData : [];
  }

  /**
   * Esta função retorna o id do usuário logado.
   */
  getUserId() {
    const user = this.getAuthData();
    return user ? user.uid : "";
  }

  /**
   * Esta função retorna o valor do campo changeDescription do usuário logado ou adiciona o campo caso não exista.
   *
   * @returns
   */
  async checkUserChangeDescription() {
    let userId = this.getUserId();
    let userData = await firestore.collection("users").doc(userId).get();

    if (userData) {
      let data = userData.data();

      if (data) {
        if (data.changeDescription == undefined) {
          await firestore.collection("users").doc(userId).update({
            changeDescription: true,
          });
          return true;
        }

        return data.changeDescription;
      }
    }
  }

  /**
   *
   * Muda o status do campo changeDescription para true ou false
   *
   * @param status
   * @returns
   */
  async updateChangeDescription(status: any): Promise<boolean> {
    let userId = this.getUserId();
    await firestore.collection("users").doc(userId).update({
      changeDescription: status,
    });

    return true;
  }

  linkAccount = (account, callback?) => {
    var provider = new firebase.auth[`${account}AuthProvider`]();
    return () => {
      var auth = this.getAuthData() as any;
      auth
        .linkWithPopup(provider)
        .then((result: any) => {
          if (
            callback &&
            result.additionalUserInfo &&
            result.additionalUserInfo.profile
          ) {
            callback(true, result);
            callback = undefined;
          }
        })
        .catch((error) => {
          if (callback) {
            callback(false, error);
            callback = undefined;
          }
        });
    };
  };

  unlinkAccount = (providerId, callback?) => {
    var auth = this.getAuthData() as any;
    auth
      .unlink(providerId)
      .then(() => callback(true))
      .catch((error) => callback(error));
  };

  /**
   * Requesita o envio do e-mail para redefinição de senha no Firebase.
   * @param {string} emailAddress - E-mail no qual será enviado a URL para redefinir a senha
   * @param {Function | undefined} callback
   */
  resetPassword(emailAddress: string, callback?) {
    var auth = firebase.auth();
    auth
      .sendPasswordResetEmail(emailAddress)
      .then(() => {
        if (callback) callback(true);
      })
      .catch((error) => {
        if (callback) callback(error);
      });
  }

  /**
   * Atualiza o e-mail do usuário no Authentication e depois na Firestore.
   * O e-mail só pode ser alterado para usuários que não são trial.
   * @param {User} user - Usuário atual que será alterado
   * @param {string} email - Novo e-mail
   * @param {Function | undefined} callback
   */
  updateProfileEmail(user: User, email: string, callback = (response) => {}) {
    const userAuth = this.getAuthData();
    if (userAuth && !user.trial)
      userAuth
        .updateEmail(email)
        .then(() => {
          firestore
            .collection("users")
            .doc(user.uid)
            .update({ email, emailVerified: false })
            .then(() => callback(true))
            .catch((error) => callback(error));
        })
        .catch((error) => callback(error));
  }

  linkCredencial(id, email, password, callback = (response) => {}) {
    const credential = firebase.auth.EmailAuthProvider.credential(
        email,
        password
      ),
      user = this.getAuthData();
    if (user)
      user
        .linkWithCredential(credential)
        .then(() => {
          firestore
            .collection("users")
            .doc(id)
            .update({ email })
            .then(() => callback(true))
            .catch((error) => callback(error));
        })
        .catch((error) => callback(error));
    else callback(false);
  }

  /**
   * Requisita o envio de um e-mail para validar se o e-mail do usuário existe.
   * @param {Function | undefined} callback
   */
  sendEmailVerification(callback = (response) => {}) {
    const user = this.getAuthData();
    if (user)
      user
        .sendEmailVerification()
        .then(() => callback(true))
        .catch((error) => callback(error));
    else callback(false);
  }

  async getUserNameAndPhoto(uid) {
    let user = await firestore.collection("users").doc(uid).get();
    let name = "";
    let photo = "";
    if (user.exists) {
      let userData = user.data();
      if (userData != undefined) {
        name = userData.displayName;
        photo = userData.photoURL;
      }
    }
    return [name, photo];
  }

  async getUsersNameAndPhotoByUid(uid) {
    let name = "";
    let photo = "";
    await firestore
      .collection("users")
      .where("uid", "<=", uid)
      .get()
      .then((data) => {
        data.forEach((doc) => {
          let userData = doc.data();
          name = userData.displayName;
          photo = userData.photoURL;
        });
      });

    return [name, photo];
  }

  /**
   * Obtém usuários da empresa, excluindo o atual.
   * Pega no bando de dados os usuários que contem na lista de usuários passados no 2º parametros
   * Verifica se o usuário atual passado no 1º parametro é administrator para liberar edição [ProfileStore.isCurrentUserAdmin]
   * Ordena a lista de usuários em ordem alfabérica pelo campo [displayName]
   * @param uid - ID do usuário atual
   * @param arrayUsers - Array com os usuários da empresa
   */
  async getUsers_(uid, arrayUsers) {
    let usersList = new Array();
    let users_ = new Array();
    let searchList;
    for (const [key] of Object.entries(arrayUsers)) {
      usersList.push(key);
    }
    for (let index = 0; index < usersList.length; index += 10) {
      searchList = usersList.slice(index, index + 10);
      await firestore
        .collection("users")
        .where("uid", "in", searchList)
        .get()
        .then((snap) => {
          let item;
          snap.forEach((doc) => {
            let data = doc.data();
            let develop = createUserModel(data);

            item = new FirebaseDocument<User>(doc.id, develop) as any;
            item.data.disabled = data.disabled;
            item.data.access = arrayUsers[item.data.uid];

            if (item.data.uid == uid) {
              if (item.data.access == 0) {
                ProfileStore.isCurrentUserAdmin = true;
              }
            }

            if (item.data.uid != uid) {
              users_.push(item);
            }
          });
        });
    }

    users_.sort((a, b) => {
      const nameA = a.data.displayName.toUpperCase();
      const nameB = b.data.displayName.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      // names must be equal
      return 0;
    });
    ProfileStore.users = users_;
    return users_;
  }

  /**
   * Obtém usuários da empresa, excluindo o atual.
   * @param uid - ID do usuário atual
   */
  async getUsers(uid) {
    let companyData = await this.fetchCompanyByUid();
    let users = companyData.users;
    ProfileStore.users = new Array();
    ProfileStore.usersSearch = new Array();

    let resp_ = await this.getUsers_(uid, users);
    // for (const user in users) {
    //   this.findUsers(user, uid, users);
    // }
    ProfileStore.usersView = resp_;
  }

  /**
   * Cria o documento de empresa no Firebase com os dados informados e
   * definindo o usuário atual como Administrador na mesma.
   * @param {string} name - Nome da empresa
   * @param {string} image - URL da imagem
   */
  async addCompany(name, image): Promise<void> {
    let uid = this.getUserId();
    let cpn = new Company(name, { [uid]: 0 }, image, now(), now());
    let addedCompany = await firestore.collection("company").add({ ...cpn });

    try {
      await firestore
        .collection("company")
        .doc(addedCompany.id)
        .update({ ...cpn });
    } catch (e) {
      console.log("erro: ", e);
      firestore.collection("company").doc(addedCompany.id).delete();
    }
  }

  /**
   * Altera nome da empresa.
   * @param {string} name - Nome da empresa
   * @param {string} companyId - Id da empresa
   */
  async updateCompany(name, companyId) {
    try {
      await firestore.collection("company").doc(companyId).update({
        name: name,
        modifiedAt: now(),
      });
    } catch (e) {
      console.log("erro: ", e);
    }
  }

  /**
   * Altera nome e imagem da empresa.
   * @param {string} name - Nome da empresa
   * @param {string} image - URL da imagem
   * @param {string} companyId - Id da empresa
   */
  async updateCompanyWithLogo(name, image, company) {
    try {
      await firestore.collection("company").doc(company).update({
        name: name,
        image: image,
        modifiedAt: now(),
      });
    } catch (e) {
      console.log("erro: ", e);
    }
  }

  /**
   * Deleta arquivo e remove a imagem do cadastro de empresa.
   * @param {object} company - Objeto contendo dados da empresa
   * @param {string} company.image - Nome da Imagem
   */
  async deleteCompanyLogo(company) {
    await this.removeCompanyImage(company.image);
    try {
      await firestore.collection("company").doc(company).update({
        image: "",
        modifiedAt: now(),
      });
    } catch (e) {
      console.log("erro: ", e);
    }
  }

  /**
   * Obtém os dados da empresa em que o usuário atual está.
   */
  async fetchCompanyByUid(): Promise<any> {
    try {
      if (LoginStore.userAuth != undefined) {
        let data = await firestore
          .collection("company")
          .where(`users.${LoginStore.userAuth.uid}`, "<", 3)
          .limit(1)
          .get();

        if (data.docs.length === 0) {
          return null;
        }

        let companyData = data.docs[0].data();
        if (companyData) {
          let company = new Company(
            companyData.name,
            companyData.users,
            companyData.image
          );
          return company;
        }
      }
    } catch (e) {
      throw Error(`Fetch Company Error: ${e}`);
    }
  }

  /**
   * Obtém o ID da empresa em que o usuário atual está.
   */
  async getCompanyID(): Promise<any> {
    try {
      if (LoginStore.userAuth != undefined) {
        let data = await firestore
          .collection("company")
          .where(`users.${LoginStore.userAuth.uid}`, "<", 3)
          .limit(1)
          .get();

        if (data.docs.length === 0) {
          return null;
        }

        let CompanyID = data.docs[0].id;
        return CompanyID;
      }
    } catch (e) {
      throw Error(`Fetch Company Error: ${e}`);
    }
  }

  /**
   * Obtém a URL para download do arquivo na pasta images/company do Storage Firebase.
   * @param {object} company - Objeto contendo dados da empresa
   * @param {string} company.imageName - Nome do arquivo
   */
  async getCompanyImage(company): Promise<string> {
    try {
      var img = storage.ref(`images/company/${company.imageName}`);
      let file = await img.getDownloadURL();
      return file;
    } catch (error) {
      console.log("error: ", error);
      return defaultImagem;
    }
  }

  /**
   * Faz upload do arquivo para pasta images/company no Storage Firebase.
   * @param {File} file - Arquivo de imagem
   * @param {string} id - Id da empresa
   */
  async addCompanyImage(file, id): Promise<void> {
    await storage
      .ref()
      .child(`images/company/${id + file.name}`)
      .put(file);
  }

  /**
   * Delete arquivo de imagem da pasta images/company no Storage Firebase
   * @param {string} file - Nome do arquivo
   */
  async removeCompanyImage(file): Promise<void> {
    if (file) {
      try {
        await storage.ref().child(`images/company/${file}`).delete();
      } catch (error) {}
    }
  }
}

const service = new ProfileService();
export { service as ProfileService };



// WEBPACK FOOTER //
// ./src/modules/profile/ProfileService.ts